import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Printer, ArrowLeft, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  primary: '#1a237e',
  accent: '#f57c00',
  green: '#2e7d32',
  red: '#c62828',
  orange: '#ef6c00',
  teal: '#00897b',
  sectionBg: '#e8eaf6',
  lightBg: '#fafbff',
};

function getStatusColor(value) {
  if (!value || value === '—') return '#9ca3af';
  const v = value.toLowerCase().trim();
  if (['yes','good','working','smooth','normal','good condition','clear','no leakage','no dents','no scratch','no dent','no'].includes(v)) return COLORS.green;
  if (['not working','damaged','hard','bad','poor','cracked'].includes(v)) return COLORS.red;
  if (['average','stained','fair'].includes(v)) return COLORS.orange;
  return '#212121';
}

export default function SheerdriveReport() {
  const { inspectionId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    fetch(`${API_URL}/api/report-templates/sheerdrive/${inspectionId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => { if (!r.ok) throw new Error('Failed to load'); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { toast.error(e.message); setLoading(false); });
  }, [inspectionId]);

  const handlePrint = () => window.print();

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/report-templates/sheerdrive/${inspectionId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Sheerdrive_Report_${data?.vehicle?.registration_number || 'report'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <Loader2 className="h-8 w-8 animate-spin text-blue-900" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <p className="text-gray-500">Inspection not found or no data available</p>
      </div>
    );
  }

  const { report_info, vehicle, inspection_categories, obd_report, total_questions, total_answers } = data;

  const vehicleFields = [
    ['Make', vehicle.make], ['Number of Owners', vehicle.num_owners],
    ['Model', vehicle.model], ['Transmission', vehicle.transmission],
    ['Year', vehicle.year], ['Registration Type', vehicle.registration_type],
    ['Body Type', vehicle.body_type], ['Registration Number', vehicle.registration_number],
    ['Fuel Type', vehicle.fuel_type], ['VIN/Chassis', vehicle.vin],
    ['Colour', vehicle.colour], ['Kms Run', vehicle.kms_run],
    ['Engine CC', vehicle.engine_cc], ['Mfg Date', vehicle.manufacturing_date],
    ['Engine Number', vehicle.engine_number], ['Reg Date', vehicle.registration_date],
  ];

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .report-section { page-break-inside: avoid; }
          .category-section { page-break-inside: avoid; }
          body { margin: 0; padding: 0; }
        }
        @page { size: A4; margin: 10mm; }
      `}</style>

      {/* Action Bar */}
      <div className="no-print sticky top-0 z-50 bg-white/95 backdrop-blur border-b shadow-sm px-6 py-3 flex items-center justify-between" data-testid="report-action-bar">
        <div className="flex items-center gap-3">
          <button onClick={() => window.history.back()} className="text-gray-600 hover:text-gray-900 transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Sheerdrive Report</h1>
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{vehicle.registration_number}</span>
          <span className="text-xs text-gray-400">{total_answers}/{total_questions} answered</span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-btn">
            <Printer className="h-4 w-4 mr-1.5" /> Print
          </Button>
          <Button size="sm" onClick={handleDownloadPdf} disabled={pdfLoading} data-testid="download-pdf-btn"
            className="bg-blue-900 hover:bg-blue-800">
            {pdfLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
            Download PDF
          </Button>
        </div>
      </div>

      {/* Report Content */}
      <div className="bg-gray-200 min-h-screen py-6 px-4">
        <div className="max-w-[210mm] mx-auto bg-white shadow-xl" style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>

          {/* PREMIUM INSPECTION HEADER */}
          <div className="p-8 report-section" data-testid="section-premium">
            <div className="flex items-center justify-between mb-5">
              <span className="font-bold text-base" style={{ color: COLORS.primary }}>{report_info.brand}</span>
              <h2 className="text-xl font-bold" style={{ color: COLORS.primary }}>Premium Inspection</h2>
            </div>

            <div className="flex gap-8 text-sm text-gray-500 mb-5">
              <div><span className="font-semibold block text-xs" style={{ color: COLORS.primary }}>Inspector</span>{report_info.inspector_name}</div>
              <div><span className="font-semibold block text-xs" style={{ color: COLORS.primary }}>Date</span>{report_info.inspection_date}</div>
              <div><span className="font-semibold block text-xs" style={{ color: COLORS.primary }}>Location</span>{report_info.location}</div>
              <div><span className="font-semibold block text-xs" style={{ color: COLORS.primary }}>Status</span><span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ backgroundColor: report_info.status === 'COMPLETED' ? '#dcfce7' : '#fef3c7', color: report_info.status === 'COMPLETED' ? '#166534' : '#92400e' }}>{report_info.status}</span></div>
            </div>

            {/* Vehicle Summary Card */}
            <div className="rounded-xl p-5 mb-6 border" style={{ backgroundColor: COLORS.lightBg }}>
              <h3 className="text-2xl font-bold mb-1" style={{ color: COLORS.primary }}>{vehicle.name}</h3>
              <div className="text-sm text-gray-500">{vehicle.registration_number} &bull; {vehicle.year} &bull; {vehicle.fuel_type} &bull; {vehicle.colour}</div>
            </div>

            {/* Vehicle Information Table */}
            <h4 className="font-bold text-sm mb-2" style={{ color: COLORS.primary }}>Vehicle Information</h4>
            <div className="border rounded-lg overflow-hidden mb-6">
              <table className="w-full text-sm">
                <tbody>
                  {Array.from({ length: Math.ceil(vehicleFields.length / 2) }, (_, i) => {
                    const left = vehicleFields[i * 2];
                    const right = vehicleFields[i * 2 + 1];
                    return (
                      <tr key={i} className="border-b border-gray-100 last:border-0">
                        <td className="py-2 px-3 text-gray-500 w-1/4 text-xs">{left?.[0]}</td>
                        <td className="py-2 px-3 font-semibold text-xs w-1/4">{left?.[1] || '—'}</td>
                        <td className="py-2 px-3 text-gray-500 w-1/4 text-xs border-l border-gray-100">{right?.[0]}</td>
                        <td className="py-2 px-3 font-semibold text-xs w-1/4">{right?.[1] || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* INSPECTION CATEGORIES */}
          {inspection_categories.map((category, catIdx) => (
            <div key={catIdx} className="px-8 pb-6 category-section" data-testid={`category-${catIdx}`}>
              {/* Category Header */}
              <div className="rounded-lg py-2.5 px-5 flex items-center justify-between mb-3" style={{ backgroundColor: COLORS.primary }}>
                <span className="text-white font-bold text-sm">{category.name}</span>
                <span className="text-white/70 text-xs">{vehicle.registration_number}</span>
              </div>

              {/* Items Table */}
              <div className="border rounded-lg overflow-hidden mb-2">
                <table className="w-full">
                  <thead>
                    <tr style={{ backgroundColor: COLORS.sectionBg }}>
                      <th className="text-left py-2 px-4 text-xs font-bold" style={{ color: COLORS.primary }}>Inspection Item</th>
                      <th className="text-left py-2 px-4 text-xs font-bold border-l" style={{ color: COLORS.primary, borderColor: '#c5cae9' }}>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {category.items.map((item, itemIdx) => (
                      <tr key={itemIdx} className={itemIdx % 2 === 1 ? 'bg-gray-50' : ''}>
                        <td className="py-2.5 px-4 text-sm text-gray-700 border-b border-gray-100">{item.question}</td>
                        <td className="py-2.5 px-4 text-sm font-semibold border-b border-gray-100 border-l" style={{ color: getStatusColor(item.answer) }}>{item.answer}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {/* OBD REPORT */}
          {obd_report && (
            <div className="p-8 report-section" data-testid="section-obd">
              <div className="py-4 px-6 rounded-lg mb-6 text-white" style={{ background: `linear-gradient(135deg, ${COLORS.teal}, ${COLORS.primary})` }}>
                <h3 className="text-lg font-bold">{obd_report.title}</h3>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm border rounded-lg p-4 mb-4">
                <div className="flex justify-between border-b border-gray-100 pb-1"><span className="text-gray-500">Report Date</span><span className="font-semibold">{obd_report.report_date}</span></div>
                <div className="flex justify-between border-b border-gray-100 pb-1"><span className="text-gray-500">Inspector</span><span className="font-semibold">{obd_report.inspector_name}</span></div>
                <div className="flex justify-between border-b border-gray-100 pb-1"><span className="text-gray-500">License Plate</span><span className="font-semibold">{obd_report.license_plate}</span></div>
                <div className="flex justify-between pb-1"><span className="text-gray-500">Vehicle</span><span className="font-semibold">{obd_report.vehicle_name}</span></div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="px-8 py-4 border-t text-xs text-gray-400 flex justify-between">
            <span>Generated by WiseDrive CRM</span>
            <span>{total_answers}/{total_questions} questions answered</span>
          </div>
        </div>
      </div>
    </>
  );
}
